import {
  FormControl,
  FormField,
  FormItem,
  useFormField,
} from '@/components/primitives/Form';
import { cn } from '@/utils/cn';
import { Select } from '@fleet/webshop-ui';
import React from 'react';
import {
  useFormContext,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from 'react-hook-form';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldContentProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends Omit<SelectFieldProps<TFieldValues, TName>, 'name'> {
  field: ControllerRenderProps<TFieldValues, TName>;
}

const SelectFieldContent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  field,
  label,
  hideLabel,
  options,
  placeholder,
  classNames,
  required,
}: SelectFieldContentProps<TFieldValues, TName>) => {
  const { error } = useFormField();

  return (
    <FormItem className={classNames?.wrapper}>
      <FormControl label={label} required={required} hideLabel={hideLabel}>
        <Select
          options={options}
          selectedKey={field.value ?? null}
          onSelectionChange={(key) => field.onChange(key as string)}
          placeholder={placeholder}
          error={!!error}
          className={cn(classNames?.select)}
        />
      </FormControl>
    </FormItem>
  );
};

export interface SelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> {
  label: React.ReactNode;
  name: TName;
  hideLabel?: boolean;
  options: SelectOption[];
  placeholder?: string;
  classNames?: { wrapper?: string; select?: string };
  required?: boolean;
}

export const SelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  name,
  ...props
}: SelectFieldProps<TFieldValues, TName>) => {
  const { control } = useFormContext<TFieldValues>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => <SelectFieldContent field={field} {...props} />}
    />
  );
};

SelectField.displayName = 'SelectField';
